import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { WizValidators } from '@commons/validators';
import { DEFAULT_LOCATION_CONFIG, LocationFieldConfig } from '@models/location/location-input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { City } from '@wizbii/models';
import { CityWebservice } from '@wizbii/webservices';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-location-input',
  templateUrl: './location-input.component.html',
  styleUrls: ['./location-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  _fieldConfig = DEFAULT_LOCATION_CONFIG;

  control = new FormControl(undefined, [WizValidators.cityRequired]);

  @Input() set fieldConfig(value: LocationFieldConfig) {
    this._fieldConfig = value;

    this.control.setValidators([
      ...(this._fieldConfig.required ? [Validators.required] : []),
      WizValidators.cityRequired,
    ]);

    this.control.updateValueAndValidity();
  }

  get fieldConfig(): LocationFieldConfig {
    return this._fieldConfig;
  }

  _value?: City;

  set value(val: City | undefined) {
    if (this._value === val) {
      return;
    }
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): City | undefined {
    return this.value;
  }

  cities$ = new Observable<City[]>();
  emptyDataError$!: Observable<boolean>;

  formSubmitSubscription?: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly cityWebservice: CityWebservice,
    private readonly controlContainer: ControlContainer
  ) {
    if (this.controlContainer?.formDirective) {
      this.formSubmitSubscription = (controlContainer.formDirective as FormGroupDirective).ngSubmit.subscribe({
        next: () => {
          this.control.markAllAsTouched();
          this.control.updateValueAndValidity();
        },
      });
    }
  }

  ngOnInit(): void {
    this.control.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      if (typeof val === 'object') {
        this.value = val;
      }
    });

    this.cities$ = this.control.valueChanges.pipe(
      untilDestroyed(this),
      debounceTime(300),
      filter((query) => query && query.length > 0),
      distinctUntilChanged(),
      switchMap((query) => this.cityWebservice.getBy({ query, countries: ['FR'] })),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.emptyDataError$ = this.cities$.pipe(map((cities) => cities?.length === 0));
  }

  onChange: (val?: City) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(val: City): void {
    this.control.setValue(val);
  }

  registerOnChange(fn: (val?: City) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  doClosed(): void {
    if (!this.fieldConfig.required || (!this.control?.errors?.cityRequired && !this.control?.errors?.required)) {
      return;
    }

    this.cities$
      .pipe(
        take(1),
        map((cities) => cities[0]),
        filter((city) => !!city)
      )
      .subscribe({
        next: (city) => {
          this.control.setValue(new City({ ...city, address: null }));
          this.control.updateValueAndValidity();
        },
      });
  }

  trackByCity = (_: number, city: City): string => city.cityPlaceId;
  displayCity = (city?: City): string => city?.short ?? '';

  ngOnDestroy(): void {
    if (this.formSubmitSubscription) {
      this.formSubmitSubscription.unsubscribe();
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    NgUpperFirstPipeModule,
  ],
  declarations: [LocationInputComponent],
  exports: [LocationInputComponent],
})
export class LocationInputModule {}
